import { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, Paper, Link, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SmsOptInForm from '../components/SmsOptInForm';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAFReJ_RueO0t9k9f';

function SmsOptInPage() {
  const [turnstileToken, setTurnstileToken] = useState('');

  const initTurnstile = useCallback(() => {
    if (window.turnstile) {
      const container = document.getElementById('turnstile-widget-sms');
      if (container && container.children.length === 0) {
        window.turnstile.render('#turnstile-widget-sms', {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setTurnstileToken(token);
          },
          'expired-callback': () => {
            setTurnstileToken('');
          },
          'error-callback': () => {
            setTurnstileToken('');
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    if (window.turnstile) {
      initTurnstile();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = initTurnstile;
      document.head.appendChild(script);
    }
  }, [initTurnstile]);

  return (
    <Box className="page-shell">
      <Container maxWidth="sm" className="page-wrap" sx={{ px: 0 }}>
        <Typography
          variant="h2"
          textAlign="center"
          sx={{ mb: 2, fontWeight: 700 }}
        >
          SMS Opt-In
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Gorty's Tech, LLC
        </Typography>

        <Paper sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Sign up for SMS updates from Gorty's Tech, LLC.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            By opting in, you agree to receive text messages from Gorty's Tech, LLC
            related to service requests, appointment scheduling, project updates,
            and customer support.
          </Typography>

          <Box className="legal-callout" sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Message frequency varies.</strong> Message and data rates may apply.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Reply <strong>HELP</strong> for help. Reply <strong>STOP</strong> to unsubscribe.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <em>Messaging consent is optional and is not required to request services.</em>
            </Typography>
          </Box>

          <SmsOptInForm turnstileToken={turnstileToken} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary" textAlign="center">
            View our{' '}
            <Link component={RouterLink} to="/privacy">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link component={RouterLink} to="/terms">
              Terms & Conditions
            </Link>
            .
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default SmsOptInPage;
